import { LanguageSwitch, t } from '@/lib/i18n';
import clsx from 'clsx';
import {
  BarChart3,
  Bell,
  Building2,
  CalendarDays,
  CalendarRange,
  ClipboardList,
  Clock,
  BookOpen,
  FolderKanban,
  Timer,
  Gauge,
  LayoutDashboard,
  LogOut,
  Menu,
  Palmtree,
  Scale,
  Settings,
  ShieldCheck,
  User,
  Users,
  Wallet,
  X,
  Globe2,
} from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { NotificationDto, Permission } from '@workforce/shared';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useTz, type TzMode } from '@/lib/timezone';
import { ThemeToggle } from '@/lib/theme';
import { Avatar } from './ui';
import { Logo } from './Logo';

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  phase?: number;
  perm?: Permission;
}

const ic = 'h-[18px] w-[18px]';

const ADMIN_NAV: NavItem[] = [
  { to: '/', label: 'Dashboard', icon: <LayoutDashboard className={ic} /> },
  { to: '/employees', label: 'Employees', icon: <Users className={ic} />, perm: 'employees.read' },
  { to: '/departments', label: 'Departments', icon: <Building2 className={ic} />, perm: 'departments.write' },
  { to: '/projects', label: 'Projects', icon: <FolderKanban className={ic} />, perm: 'projects.read' },
  { to: '/project-hours', label: 'Project Hours', icon: <Timer className={ic} />, perm: 'projects.read' },
  { to: '/attendance', label: 'Attendance', icon: <Clock className={ic} />, perm: 'attendance.read' },
  { to: '/leave', label: 'Leave', icon: <Palmtree className={ic} /> },
  { to: '/balances', label: 'Vacation Balances', icon: <Scale className={ic} /> },
  { to: '/productivity', label: 'Productivity', icon: <Gauge className={ic} />, perm: 'productivity.read' },
  { to: '/reports', label: 'Reports', icon: <BarChart3 className={ic} />, perm: 'reports.read' },
  { to: '/payroll', label: 'Payroll', icon: <Wallet className={ic} />, perm: 'payroll.manage' },
  { to: '/schedules', label: 'Schedules', icon: <CalendarRange className={ic} />, perm: 'schedules.write' },
  { to: '/holidays', label: 'Holidays', icon: <CalendarDays className={ic} />, perm: 'holidays.write' },
  { to: '/settings', label: 'Settings', icon: <Settings className={ic} />, perm: 'settings.write' },
  { to: '/audit', label: 'Audit Log', icon: <ShieldCheck className={ic} />, perm: 'audit.read' },
];

const MANAGER_NAV: NavItem[] = [
  { to: '/', label: 'Dashboard', icon: <LayoutDashboard className={ic} /> },
  { to: '/employees', label: 'My Team', icon: <Users className={ic} /> },
  { to: '/projects', label: 'Projects', icon: <FolderKanban className={ic} />, perm: 'projects.read' },
  { to: '/project-hours', label: 'Project Hours', icon: <Timer className={ic} />, perm: 'projects.read' },
  { to: '/attendance', label: 'Team Attendance', icon: <Clock className={ic} />, perm: 'attendance.read' },
  { to: '/leave', label: 'Leave Approvals', icon: <Palmtree className={ic} /> },
  { to: '/balances', label: 'Team Balances', icon: <Scale className={ic} /> },
  { to: '/productivity', label: 'Team Productivity', icon: <Gauge className={ic} /> },
  { to: '/reports', label: 'Team Reports', icon: <BarChart3 className={ic} />, perm: 'reports.read' },
];

const SELF_NAV: NavItem[] = [
  { to: '/me/attendance', label: 'My Attendance', icon: <Clock className={ic} /> },
  { to: '/me/leave', label: 'My Leave', icon: <Palmtree className={ic} /> },
  { to: '/me/balance', label: 'My Balance', icon: <ClipboardList className={ic} /> },
  { to: '/me/productivity', label: 'My Productivity', icon: <Gauge className={ic} /> },
  { to: '/profile', label: 'My Profile', icon: <User className={ic} /> },
  { to: '/policy', label: 'Internal Policy', icon: <BookOpen className={ic} /> },
];

function NavSection({ title, items, onNavigate }: { title?: string; items: NavItem[]; onNavigate: () => void }) {
  return (
    <div className="space-y-0.5">
      {title && <p className="px-3 pb-1 pt-4 text-[11px] font-semibold uppercase tracking-wider text-slate-400">{t(title)}</p>}
      {items.map((i) => (
        <NavLink
          key={i.to}
          to={i.to}
          end={i.to === '/'}
          onClick={onNavigate}
          className={({ isActive }) =>
            clsx(
              'group flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
              isActive ? 'bg-brand-50 text-brand-700' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900',
            )
          }
        >
          {i.icon}
          <span className="flex-1 truncate">{t(i.label)}</span>
          {i.phase && (
            <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-400 group-hover:bg-surface">
              P{i.phase}
            </span>
          )}
        </NavLink>
      ))}
    </div>
  );
}

function TzSwitch() {
  const { mode, setMode, companyZone, viewerZone } = useTz();
  const opts: { v: TzMode; label: string; title: string }[] = [
    { v: 'employee', label: t('Employee time'), title: t("Show each record in that employee's own time zone") },
    { v: 'company', label: t('Company time'), title: t('Show everything in company time ({zone})', { zone: companyZone }) },
    { v: 'mine', label: t('My time'), title: t('Show everything in your time zone ({zone})', { zone: viewerZone }) },
  ];
  return (
    <div className="hidden items-center gap-1.5 md:flex" title={t('Time display')}>
      <Globe2 className="h-4 w-4 text-slate-400" />
      <select
        value={mode}
        onChange={(e) => setMode(e.target.value as TzMode)}
        className="rounded-md border-0 bg-transparent py-1 ps-1 pe-7 text-xs font-medium text-slate-600 focus:ring-2 focus:ring-brand-500"
        aria-label={t('Time display mode')}
      >
        {opts.map((o) => (
          <option key={o.v} value={o.v} title={o.title}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function Notifications() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const nav = useNavigate();
  const qc = useQueryClient();
  const { fmt } = useTz();
  const q = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api.get<{ items: NotificationDto[]; unreadCount: number }>('/notifications?limit=15'),
    refetchInterval: 60_000,
  });
  const markRead = useMutation({
    mutationFn: (id: string) => api.post(`/notifications/${id}/read`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
  const markAll = useMutation({
    mutationFn: () => api.post('/notifications/read-all'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
  useEffect(() => {
    const close = (e: MouseEvent) => ref.current && !ref.current.contains(e.target as Node) && setOpen(false);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);
  const unread = q.data?.unreadCount ?? 0;
  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
        aria-label={unread ? t('Notifications, {n} unread', { n: unread }) : t('Notifications')}
      >
        <Bell className="h-5 w-5" />
        {unread > 0 && (
          <span className="absolute end-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute end-0 z-40 mt-2 w-[min(22rem,calc(100vw-2rem))] overflow-hidden rounded-xl border border-slate-200 bg-surface shadow-lg">
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5">
            <p className="text-sm font-semibold">{t('Notifications')}</p>
            {unread > 0 && (
              <button className="text-xs font-medium text-brand-600 hover:underline" onClick={() => markAll.mutate()}>
                {t('Mark all read')}
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {!q.data?.items.length && <p className="px-4 py-8 text-center text-sm text-slate-500">{t("You're all caught up.")}</p>}
            {q.data?.items.map((n) => (
              <button
                key={n.id}
                onClick={() => {
                  if (!n.readAt) markRead.mutate(n.id);
                  if (n.link) nav(n.link);
                  setOpen(false);
                }}
                className={clsx('block w-full border-b border-slate-50 px-4 py-3 text-start hover:bg-slate-50', !n.readAt && 'bg-brand-50/40')}
              >
                <div className="flex items-start gap-2">
                  {!n.readAt && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-brand-500" />}
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-800">{t(n.title)}</p>
                    {n.body && <p className="mt-0.5 line-clamp-2 text-xs text-slate-500">{n.body.split('\n').map((line) => t(line)).join('\n')}</p>}
                    <p className="mt-1 text-[11px] text-slate-400">{fmt(n.createdAt, null, 'dd LLL, HH:mm')}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function Layout() {
  const { user, logout, can } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const loc = useLocation();
  useEffect(() => setMobileOpen(false), [loc.pathname]);
  if (!user) return null;

  const main =
    user.role === 'admin'
      ? ADMIN_NAV.filter((i) => !i.perm || can(i.perm))
      : user.role === 'manager'
        ? MANAGER_NAV
        : [{ to: '/', label: 'Dashboard', icon: <LayoutDashboard className={ic} /> }];
  const showSelf = !!user.employee;
  const displayName = user.employee?.fullName ?? user.email;

  const sidebar = (
    <div className="flex h-full flex-col">
      <Link to="/" className="block px-5 py-5" title={user.companyName}>
        <Logo variant="mark" version={user.companyLogoVersion} alt={user.companyName} className="h-8 w-auto max-w-[10rem]" />
        <p className="mt-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Workforce</p>
      </Link>
      <nav className="flex-1 overflow-y-auto px-3 pb-4">
        <NavSection items={main} onNavigate={() => setMobileOpen(false)} />
        {showSelf && (
          <NavSection
            title={user.role === 'employee' ? undefined : 'Me'}
            items={SELF_NAV}
            onNavigate={() => setMobileOpen(false)}
          />
        )}
        {!showSelf && <NavSection title="Me" items={[SELF_NAV[4]!, SELF_NAV[5]!]} onNavigate={() => setMobileOpen(false)} />}
      </nav>
      <div className="border-t border-slate-100 p-3">
        <LanguageSwitch className="mb-2 sm:hidden" />
        <div className="flex items-center gap-3 rounded-lg px-2 py-2">
          <Avatar name={displayName} size="sm" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-slate-800">{displayName}</p>
            <p className="truncate text-xs capitalize text-slate-500">{t(user.role === 'admin' ? 'Administrator' : user.role === 'manager' ? 'Manager' : 'Employee')}</p>
          </div>
          <button onClick={() => void logout()} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700" aria-label={t('Sign out')} title={t('Sign out')}>
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 start-0 z-30 hidden w-64 border-e border-slate-200 bg-surface lg:block print:!hidden">{sidebar}</aside>
      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <aside className="absolute inset-y-0 start-0 w-72 max-w-[85vw] bg-surface shadow-xl">
            <button className="absolute end-3 top-4 rounded-lg p-1.5 text-slate-400 hover:bg-slate-100" onClick={() => setMobileOpen(false)} aria-label={t('Close menu')}>
              <X className="h-5 w-5" />
            </button>
            {sidebar}
          </aside>
        </div>
      )}

      <div className="lg:ps-64 print:!pl-0">
        <header className="sticky top-0 z-20 flex h-14 print:!hidden items-center gap-3 border-b border-slate-200 bg-surface/90 px-4 backdrop-blur sm:px-6">
          <button className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 lg:hidden" onClick={() => setMobileOpen(true)} aria-label={t('Open menu')}>
            <Menu className="h-5 w-5" />
          </button>
          <div className="flex-1" />
          <LanguageSwitch className="hidden sm:inline-flex" />
          {user.role !== 'employee' && <TzSwitch />}
          <ThemeToggle />
          <Notifications />
          <Link to="/profile" className="rounded-lg p-1 hover:bg-slate-100" aria-label={t('My profile')}>
            <Avatar name={displayName} size="sm" />
          </Link>
        </header>
        <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

